import axios from 'axios';
import { config } from '../config';
import { isPreviewMode, previewHeaders } from './previewMode';

// Umbraco Delivery API uses Start-Item header to scope content to a specific site.
// In an Umbraco preview the token header is added too, which is what makes the API return
// draft content; the CMS refuses it unless Start-Item names the site it was issued for.
const getHeaders = (): Record<string, string> => {
  const sitePath = config.sitePath;
  return {
    ...(sitePath ? { 'Start-Item': sitePath } : {}),
    ...previewHeaders(),
  };
};

export interface ParsedBlock {
  contentType: string;
  id: string;
  properties: any;
  areas?: ParsedArea[];
  rowSpan?: number;
  columnSpan?: number;
}

export interface ParsedArea {
  alias: string;
  items: ParsedBlock[];
  rowSpan?: number;
  columnSpan?: number;
}

export interface PageContent {
  contentType: string;
  name: string;
  id: string;
  route: {
    path: string;
  };
  properties: any;
}

const parseBlockGridFromApi = (blockGrid: any): ParsedBlock[] => {
  if (!blockGrid || !blockGrid.items) return [];

  return blockGrid.items.map((item: any) => {
    const block: ParsedBlock = {
      contentType: item.content?.contentType || '',
      id: item.content?.id || '',
      properties: item.content?.properties || {},
      rowSpan: item.rowSpan,
      columnSpan: item.columnSpan,
    };

    // Parse nested areas
    if (item.areas && Array.isArray(item.areas)) {
      block.areas = item.areas.map((area: any) => ({
        alias: area.alias,
        items: area.items ? parseBlockGridFromApi({ items: area.items }) : [],
        rowSpan: area.rowSpan,
        columnSpan: area.columnSpan,
      }));
    }

    return block;
  });
};

// Session-level cache for content endpoints. The same content is fetched by
// multiple components (SiteSettingsContext + HomePage both call getFrontPage
// on initial load); keep a Promise-based cache to dedupe concurrent requests
// and to reuse data across route re-mounts. An inline script in index.html
// seeds window.__UMBRACO_PREFETCH__ with fetches that start before the JS
// bundle parses — we consume them here when available.
const contentCache = new Map<string, Promise<PageContent>>();

interface PrefetchedWindow extends Window {
  __UMBRACO_PREFETCH__?: Record<string, Promise<PageContent | null> | undefined>;
}

const fetchFromApi = (path: string): Promise<PageContent> =>
  axios
    .get(`${config.umbracoApiUrl}/umbraco/delivery/api/v2/content/item${path}`, { headers: getHeaders() })
    .then((r) => r.data as PageContent);

const resolveWithCache = (cacheKey: string, prefetchKey: string, path: string): Promise<PageContent> => {
  // Neither cache is usable in preview: the index.html prefetch deliberately asked for
  // published content (it runs before the token is read), and the draft changes every time
  // the editor saves, so a re-visited route has to be refetched.
  if (isPreviewMode()) {
    return fetchFromApi(path);
  }

  const existing = contentCache.get(cacheKey);
  if (existing) return existing;
  const prefetched = (window as PrefetchedWindow).__UMBRACO_PREFETCH__?.[prefetchKey];
  const promise: Promise<PageContent> = prefetched
    ? prefetched.then((d) => d || fetchFromApi(path)).catch(() => fetchFromApi(path))
    : fetchFromApi(path);
  contentCache.set(cacheKey, promise);
  return promise;
};

const getFrontPage = (): Promise<PageContent> => resolveWithCache('frontPage', '/', '/');

const getContentByPath = (path: string): Promise<PageContent> => resolveWithCache(`path:${path}`, path, path);

export interface ShopProductPage {
  name: string;
  route: string;
  sku: string;
  brand?: string;
  shortDescription?: string;
  isNew?: boolean;
  productImages?: Array<{ url: string; name?: string }>;
  externalImageUrl?: string;
}

/** Fetch all shopProductPage content items from Umbraco. */
const getShopProducts = async (): Promise<ShopProductPage[]> => {
  const response = await axios.get(
    `${config.umbracoApiUrl}/umbraco/delivery/api/v2/content`,
    {
      headers: getHeaders(),
      params: {
        filter: 'contentType:shopProductPage',
        sort: 'sortOrder:asc',
        take: 100,
      },
    }
  );

  const items = response.data?.items || [];
  return items.map((item: any) => ({
    name: item.name,
    route: item.route?.path || '',
    sku: item.properties?.sku || '',
    brand: item.properties?.brand,
    shortDescription: item.properties?.shortDescription,
    isNew: item.properties?.isNew || false,
    productImages: parseMediaPicker(item.properties?.productImages),
    externalImageUrl: item.properties?.externalImageUrl || undefined,
  }));
};

/** Parse Umbraco MediaPicker3 output into simple URL array. */
const parseMediaPicker = (media: any): Array<{ url: string; name?: string }> => {
  if (!media) return [];
  const items = Array.isArray(media) ? media : [media];
  return items
    .filter((m: any) => m?.url)
    .map((m: any) => ({ url: m.url, name: m.name }));
};

/** Parse Block List structure (simpler than Block Grid — flat array of blocks). */
const parseBlockListFromApi = (blockList: any): ParsedBlock[] => {
  if (!blockList || !blockList.items) return [];

  return blockList.items.map((item: any) => ({
    contentType: item.content?.contentType || '',
    id: item.content?.id || '',
    properties: item.content?.properties || {},
  }));
};

export const umbracoApi = {
  getFrontPage,
  getContentByPath,
  getShopProducts,
  parseBlockGridFromApi,
  parseBlockListFromApi,
};
